import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "lib/hooks/useLocale";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import CouponDialog from "components/checkout/dialogs/CouponDialog";
import {
  deleteRequest,
  getRequest,
  postRequest,
  staticOptions,
  kratosPublicFetcher,
  BaseApi,
} from "lib/core/apiClient";
import {
  getItems,
  findIdCouponByCode,
  isOnlyFreebieAutoApplied,
  appendParamsToItemsUrl,
} from "lib/utils/checkout";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { useAuthContext } from "lib/contexts/AuthProvider";
import useSWR, { mutate } from "swr";
import { errorToast } from "lib/utils/toasters";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents, apiURI, CombDiscountConflictCode, eventTypes } from "lib/utils/constants";
import CelebrationPopup from "components/common/popups/CelebrationPopup";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { CheckoutModalType, CouponType, SavingItemsType, ShippingHandlesType } from "lib/types/checkout";
import { ErrorType } from "lib/types/address";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useSearchParams } from "react-router-dom";
import { getInvalidityReason, publishPostMessage } from "lib/utils/helpers";
import SwitchCouponDialog from "components/checkout/dialogs/SwitchCouponDialog";
import LoaderDialog from "./dialogs/LoaderDialog";
import CouponSummaryStrip from "components/common/CouponSummaryStrip";
import DiscountedProductSelectorPopup from "components/common/popups/DiscountedProductSelectorPopup";
import { usePrevious } from "lib/hooks/usePrevious";
import { AnimateLoading } from "components/animation/AnimateLoading";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { useUserContext } from "lib/contexts/UserProvider";

import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import useRewards from "lib/hooks/useRewards";
import CouponRemovableTile from "components/coupons/tiles/CouponRemovableTile";
import MaxDiscountCouponTile from "components/coupons/tiles/MaxDiscountCouponTile";
import CouponInputField from "components/coupons/tiles/CouponInputField";
import CouponStrip from "components/coupons/tiles/CouponStrip";
import MultiGiftCard from "components/coupons/cards/MultiGiftCard";
import ViewOtherCouponsTile from "components/coupons/tiles/ViewCouponsTile";
import { CouponDisplayConfig } from "lib/types/merchantConfigTypes";
import type { AvailableCoupons } from "components/coupons/couponProps";

export type { AvailableCoupons };

interface CouponsProps {
  context?: "cart" | "checkout";
  loading?: boolean;
}

const CouponTags = ["shopflo", "platform", "ai_generated"];

const Coupons: React.FC<CouponsProps> = ({ context = "checkout", loading = false }) => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page")?.toLowerCase();
  const {
    state: {
      appliedCoupons,
      checkoutId,
      coupons,
      isC2P,
      checkoutModal,
      checkoutView,
      discountedProductSelectorData,
      checkoutItems,
      discountProductSelectorModal,
      shippingHandles,
      couponReplacementConfig,
      partnerOffers,
    },
    actions: {
      updateCheckoutBasedOnCheckoutResponse,
      setCoupons,
      setCheckoutItems,
      setBankOffers,
      setPartnerOffers,
      setPartnerOffersDisplay,
      setCheckoutModal,
      setCheckoutView,
      setShippingHandles,
      setCouponReplacementConfig,
      hasCelebrationPopupBeenShown,
      markCelebrationPopupShown,
    },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated, lockDiscounts },
  } = useAuthContext();
  const {
    state: { merchant, cartMetadata, checkoutMetadata },
  } = useMerchantContext();
  const {
    actions: { mutatePayment },
  } = usePaymentContext();
  const {
    state: { user },
  } = useUserContext();
  const { openPopup, closePopup, activePopup, enqueueAuto } = usePopupOrchestrator();

  const { initialCheckoutStep, uiAttributes } = checkoutMetadata ?? {};
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [appliedCouponCode, setAppliedCouponCode] = useState<string>("");
  const [incomingCouponCode, setIncomingCouponCode] = useState<string>("");
  const closeCelebrationPopup = useCallback(() => {
    closePopup();
    setAppliedCouponCode("");
    setIncomingCouponCode("");
  }, [closePopup]);
  const [couponApplyError, setCouponApplyError] = useState<ErrorType>({ status: false, message: "" });
  const [couponDialogFilter, setCouponDialogFilter] = useState<string>("ALL");
  const isManualCouponCelebrationShown = useRef(false);
  const shouldFetchCoupons = !lockDiscounts && !isC2P && Boolean(checkoutId);
  const isAuthenticatedUser = Boolean(isAuthenticated);
  const hiddenElements =
    context === "cart"
      ? cartMetadata?.uiAttributes?.layout?.hiddenElements
      : checkoutMetadata?.uiAttributes?.layout?.hiddenElements;
  const couponDisplayConfig =
    context === "cart" ? cartMetadata?.couponDisplayConfig : checkoutMetadata?.couponDisplayConfig;
  const discountConfig =
    context === "cart" ? cartMetadata?.uiAttributes?.discountConfig : checkoutMetadata?.uiAttributes?.discountConfig;
  const partnerOffersConfig =
    context === "cart" ? cartMetadata?.uiAttributes?.partnerOffers : checkoutMetadata?.uiAttributes?.partnerOffers;

  const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
  const PG_OFFER_PATH = appendParamsToItemsUrl(`/${checkoutId}/bank-offers`);
  const OTHER_OFFERS_PATH = checkoutId ? `/checkout/${checkoutId}/other-offers` : null;
  const {
    state: { isRewardsReady },
  } = useRewards();

  const { data: couponsData, isValidating: isCouponsLoading } = useSWR(
    () => {
      return shouldFetchCoupons ? DISCOUNT_PATH : null;
    },
    (url: string) => {
      return isAuthenticatedUser ? getRequest(url, "KRATOS_PRIVATE") : kratosPublicFetcher(url);
    },
    staticOptions,
  );

  const { data: bankOffersData, isValidating: isBankOffersLoading } = useSWR(
    () => {
      if (!uiAttributes.discountBankOffers) return
      return shouldFetchCoupons ? PG_OFFER_PATH : null;
    },
    (url: string) => {
      return kratosPublicFetcher(url);
    },
    staticOptions,
  );

  const isDiscountsReady = !shouldFetchCoupons || couponsData !== undefined;

  const { data: otherOffersData, mutate: mutateOtherOffers } = useSWR(
    () => {
      if (!partnerOffersConfig?.enabled || !isAuthenticatedUser || !isRewardsReady || !isDiscountsReady) return null;
      // Intentionally not gated on shouldFetchCoupons (lockDiscounts / isC2P): prefetch
      // so the partner-offers strip is ready the moment discounts unlock. Missing
      // checkoutId is still safe — OTHER_OFFERS_PATH is null then.
      return OTHER_OFFERS_PATH;
    },
    (url: string) => getRequest(url, "KRATOS_PRIVATE"),
    staticOptions,
  );

  useEffect(() => {
    if (otherOffersData === undefined) return;
    const sorted = [...(otherOffersData?.offers ?? [])].sort((a, b) => a.priority - b.priority);
    setPartnerOffers(sorted);
    setPartnerOffersDisplay(otherOffersData?.display ?? null);
  }, [otherOffersData]);
  const previousCheckoutItems = usePrevious(checkoutItems);
  useEffect(() => {
    if (previousCheckoutItems === undefined || checkoutItems === previousCheckoutItems) return;
    if (OTHER_OFFERS_PATH) mutate(OTHER_OFFERS_PATH);
  }, [checkoutItems, previousCheckoutItems, OTHER_OFFERS_PATH]);

  const openPartnerOffersDialog = async () => {
    try {
      setIsLoading(true);
      await mutateOtherOffers();
      setCouponDialogFilter("PARTNER_OFFERS");
      setCheckoutModal("DISCOUNT_LIST");
    } finally {
      setIsLoading(false);
    }
  };

  // Revalidate bank-offers whenever discount data is updated (including when DISCOUNT_PATH
  // is mutated from other components), so both caches stay in sync without changing other files.
  useEffect(() => {
    if (couponsData !== undefined && PG_OFFER_PATH) {
      mutate(PG_OFFER_PATH);
    }
  }, [couponsData, PG_OFFER_PATH]);

  const selectedShippingHandle = shippingHandles?.find((handle) => handle.selected_handle);

  useEffect(() => {
    if (!Boolean(couponsData) || Boolean(isCouponsLoading)) return;
    setCoupons(couponsData);
  }, [couponsData, isCouponsLoading]);

  useEffect(() => {
    if (!Boolean(bankOffersData) || Boolean(isBankOffersLoading)) return;
    setBankOffers(bankOffersData.map((offer: any) => ({
        coupon_details: {
          ...offer,
          coupon_type: CouponType.PG_OFFERS,
        },
    })));
  }, [bankOffersData, isBankOffersLoading]);

  useEffect(() => {
    const hasAutoAppliedCoupon = appliedCoupons?.find((coupon) => coupon.autoApplied);
    const hasShippingDiscount = appliedCoupons?.find((coupon) => coupon.couponType === "SHIPPING");

    if (!hasCelebrationPopupBeenShown() && hasShippingDiscount) {
      setIncomingCouponCode(hasShippingDiscount?.code ?? "");
      openDelayedCelebration();
    }

    if (!hasAutoAppliedCoupon || hasCelebrationPopupBeenShown()) return;

    const hasProductSelectorDiscount = Boolean(
      discountedProductSelectorData?.items?.length &&
      discountedProductSelectorData?.totalItems > discountedProductSelectorData?.totalSelectedItems,
    );

    const hasDiscountedProductSelectorItems = checkoutItems?.some((item) => item.hasProductSelector);

    if (Boolean(hasAutoAppliedCoupon) && !hasCelebrationPopupBeenShown()) {
      setIncomingCouponCode(hasAutoAppliedCoupon?.code ?? "");
      if (hasProductSelectorDiscount && !hasDiscountedProductSelectorItems) {
        // Initial selector is enqueued once from checkoutInit (AUTO).
        return;
      }
      openDelayedCelebration();
      markCelebrationPopupShown();
    }
  }, [appliedCoupons, checkoutItems, discountedProductSelectorData, hasCelebrationPopupBeenShown, markCelebrationPopupShown]);

  const monitorChange = (codes: string[], previousCodes?: string[]) => {
    return codes.find((code) => !previousCodes?.includes(code));
  };

  const handleNewFreebie = (code: string) => {
    setIncomingCouponCode(code);
    if (
      discountProductSelectorModal === "NONE"    ) {
      openDelayedCelebration();
    }
  };

  const openDelayedCelebration = () => {
    enqueueAuto("CELEBRATION", {});
  };

  const autoAppliedFreebieIds = appliedCoupons
    .filter((coupon) => coupon.autoApplied)
    .map((coupon) => coupon.code);

  const previousAutoAppliedFreebieIds = usePrevious(autoAppliedFreebieIds);

  useEffect(() => {
    const changedCode = monitorChange(autoAppliedFreebieIds, previousAutoAppliedFreebieIds);
    if (changedCode) {
      handleNewFreebie(changedCode);
    }
  }, [appliedCoupons]);

  const savingItems: SavingItemsType = useMemo(() => {
    let count = 0;
    let manualFreebieCount = 0;
    const savings = appliedCoupons?.reduce(
      (
        acc: number,
        {
          discountValue,
          isFreebie,
          autoApplied,
          discountPercentage,
          isReward,
          couponRewardConfig,
          isRemovable,
          couponType,
        },
      ) => {
        if (isReward) {
          return acc;
        }
        if (!isFreebie) {
          count++;
        }
        if (isFreebie) {
          if (discountPercentage !== 100) {
            count++;
          } else if (!autoApplied || isRemovable) {
            manualFreebieCount++;
            count++;
          }
        }
        if (isReward) {
          const discountAmount = couponRewardConfig?.amount ?? 0;
          return acc + discountAmount;
        }

        if (couponType === CouponType.SHIPPING && selectedShippingHandle) {
          const originalPrice = parseFloat(selectedShippingHandle?.original_price || "0");
          const currentPrice = parseFloat(selectedShippingHandle?.price || "0");
          return acc + (originalPrice - currentPrice);
        }

        return acc + discountValue;
      },
      0,
    );
    return { savings: savings, count: count, manualFreebieCount: manualFreebieCount };
  }, [appliedCoupons, coupons, checkoutView]);

  const availableCoupons = useMemo(() => {
    let couponDetails: AvailableCoupons = { shopflo: 0, shopify: 0 };
    if (!coupons) return couponDetails;

    const applicableCoupons = coupons.filter(
      (coupon: any) => coupon?.applicable || coupon?.un_applicability_reason?.reason === "UARC_002",
    );

    couponDetails.shopify = applicableCoupons.filter((coupon: any) =>
      coupon?.coupon_details?.tags?.includes("SHOPIFY"),
    ).length;

    couponDetails.shopflo = applicableCoupons.filter((coupon: any) =>
      coupon?.coupon_details?.tags?.some((tag: string) => CouponTags.includes(tag.toLowerCase())),
    ).length;

    return couponDetails;
  }, [coupons]);

  const removableAppliedCoupon = useMemo(() => {
    if (!merchant?.discountSettings?.showMaxDiscount) return null;
    if (appliedCoupons.length < 1) return null;

    const sortedAppliedCoupons = appliedCoupons
      .filter((coupon) => !coupon?.isFreebie && !coupon?.isPrepaid)
      .sort((coupon, compareCoupon) => (coupon?.discountValue < compareCoupon?.discountValue ? 1 : -1))
      .sort((coupon, compareCoupon) => (coupon?.isRemovable ? -1 : compareCoupon?.isRemovable ? 1 : 0));

    return sortedAppliedCoupons?.[0];
  }, [appliedCoupons]);

  const maxAvailableCoupon = useMemo(() => {
    if (!merchant?.discountSettings?.showMaxDiscount) return null;
    if (availableCoupons.shopflo < 1) return null;

    const applicableCoupons = coupons.filter(
      (coupon) =>
        Boolean(
          coupon?.coupon_details?.tags?.includes("SHOPFLO") ||
            coupon?.coupon_details?.tags?.includes("PLATFORM") ||
            coupon?.coupon_details?.tags?.includes("AI_GENERATED"),
        ) &&
        Boolean(coupon?.applicable) &&
        !Boolean(coupon?.coupon_details?.already_applied) &&
        Boolean(coupon?.coupon_details?.coupon_type !== "PREPAID") &&
        Boolean(coupon?.coupon_details?.coupon_type !== "BXGY") &&
        Boolean(coupon?.coupon_details?.coupon_type !== "CALLOUT_CARD"),
    );

    if (applicableCoupons.length < 1) return null;

    return applicableCoupons?.sort((coupon, compareCoupon) =>
      coupon?.total_discount < compareCoupon?.total_discount ? 1 : -1,
    )?.[0];
  }, [coupons, appliedCoupons]);

  //Check if the coupons are enabled for particluar page
  const currentCouponDisplayConfig = couponDisplayConfig?.find((config: CouponDisplayConfig) => {
    if (checkoutView === "ADDRESS_LIST") {
      return config.type === "ORDER_SUMMARY";
    } else if (page === "cart") {
      return config.type === "CART";
    } else {
      return config.type === checkoutView;
    }
  });

  const getCouponDisplayType = useCallback(() => {
    const areCouponsEnabled = Boolean(currentCouponDisplayConfig?.isDisplayEnabled);

    if (!areCouponsEnabled || merchant?.couponStackLength === 0) return null;

    const displayType = currentCouponDisplayConfig?.displayType;
    const isCouponDialogDisabled = !merchant?.isDiscountEnabled;

    if (isCouponDialogDisabled) {
      return null;
    }

    return displayType;
  }, [coupons, merchant, currentCouponDisplayConfig]);

  const showCoupons = getCouponDisplayType() && !isC2P;
  const showCouponSummaryStrip = appliedCoupons?.length > 0 && !isOnlyFreebieAutoApplied(appliedCoupons);

  const shouldShowSavings = () => {
    const displayType = getCouponDisplayType();
    const hasStripWithoutCoupons = displayType === "STRIP" && !coupons?.length;
    const isNotStripDisplay = displayType !== "STRIP";
    const hasMaxDiscountDisabled = !merchant?.discountSettings?.showMaxDiscount;

    const hasMultipleCouponsOrFreebies =
      displayType === "STRIP" &&
      appliedCoupons?.length > 0 &&
      (maxAvailableCoupon || appliedCoupons?.length > 1 || savingItems.manualFreebieCount > 0);

    return (
      hasStripWithoutCoupons || isNotStripDisplay || hasMaxDiscountDisabled || hasMultipleCouponsOrFreebies
    );
  };

  const shouldShowAppliedCoupons = () => {
    const displayType = getCouponDisplayType();

    return (
      displayType === "STRIP" &&
      appliedCoupons?.length > 0 &&
      ((!coupons?.length && appliedCoupons?.length > 0) ||
        maxAvailableCoupon ||
        appliedCoupons?.length > 1 ||
        savingItems.manualFreebieCount > 0)
    );
  };

  const handleDiscountApply = async (
    code: string,
    isManual: boolean = false,
    overrideRemoval: boolean = false,
    codeTitle?: string,
  ): Promise<boolean> => {
    if (!code?.trim()) return false;

    const couponId = findIdCouponByCode(coupons, code);

    sendAnalyticsEvent({
      eventName: isManual ? analyticsEvents.FLO_COUPON_ENTERED : analyticsEvents.FLO_COUPON_SELECTED,
      eventType: "click",
      metaData: {
        couponData: {
          coupon_code: code,
          coupon_id: couponId || "NA",
        },
      },
    });

    const payload = {
      discount_code: code?.trim(),
    };

    try {
      if (overrideRemoval) {
        setCheckoutModal("LOADER");
      } else {
        setIsLoading(true);
      }

      let response;
      const pathWithParams = appendParamsToItemsUrl(DISCOUNT_PATH, {
        ...(overrideRemoval && { raise_on_removal: "false" }),
      });

      if (isAuthenticated) {
        response = await postRequest(pathWithParams, payload, "KRATOS_PRIVATE");
        const discountResponse = await getRequest(DISCOUNT_PATH, "KRATOS_PRIVATE");
        setCoupons(discountResponse);
      } else {
        response = await postRequest(pathWithParams, payload, "KRATOS_PUBLIC");
        const discountResponse = await getRequest(DISCOUNT_PATH, "KRATOS_PUBLIC");
        setCoupons(discountResponse);
      }

      if (!response) {
        errorToast(t("coupon_not_found"));
        return false;
      }

      if (response?.metadata?.show_discount_warning) {
        setCouponReplacementConfig({
          appliedCouponTitle: codeTitle,
          showPopup: Boolean(response?.metadata?.show_discount_warning),
          appliedDiscountCode: code?.trim(),
          invalidDiscountCodes: response?.metadata?.invalid_discount_code || [],
          invalidityReason: getInvalidityReason(response?.metadata?.invalid_discount_reasons || []),
        });

        overrideRemoval ? setCheckoutModal("NONE") : setIsLoading(false);

        return false;
      }

      if (context === "checkout") {
        publishPostMessage(eventTypes.COUPONS_UPDATED, {});
      }

      setAppliedCouponCode(code);
      setIncomingCouponCode("");
      setCouponApplyError({ status: false, message: "" });

      const showProductSelector = updateCheckoutBasedOnCheckoutResponse(response, "MAIN");

      overrideRemoval ? setCheckoutModal("NONE") : setIsLoading(false);
      if (!showProductSelector) {
        setCheckoutModal("NONE");
      }
      if (!showProductSelector && !isManualCouponCelebrationShown.current) {
        setCheckoutModal("NONE");
        openPopup("CELEBRATION", {});
        isManualCouponCelebrationShown.current = true;
      }

      const isAddressServiceable = response?.pricing?.serviceable ?? false;
      if (isAuthenticated && !isAddressServiceable && context !== "cart") {
        const hasUserAddresses = user?.addresses?.find((address) => address?.source === "SHIPPING_ADDRESS");

        if (checkoutView === "PAYMENTS" && initialCheckoutStep !== "PAYMENTS") {
          setCheckoutView("ADDRESS_LIST");
        }

        if (checkoutView === "PAYMENTS" && initialCheckoutStep === "PAYMENTS") {
          openPopup(hasUserAddresses ? "ADDRESS_LIST" : "ADDRESS_NEW", {});
        }

        errorToast(t(hasUserAddresses ? "serviceability_error" : "full_address"), 5000);
        return false;
      }

      const shippingHandles = response?.metadata?.available_shipping_handles ?? [];
      const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
      setShippingHandles(shippingHandles as ShippingHandlesType);

      if (
        shippingHandles.length > 0 &&
        showShippingHandles &&
        checkoutView === "PAYMENTS" &&
        initialCheckoutStep !== "PAYMENTS"
      ) {
        openPopup("SHIPPING_HANDLES", {});
      } else {
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        mutate(`UPI_INTENT`);
      }

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_COUPON_SUCCESS,
        eventType: "flo_action",
        metaData: {
          couponData: {
            coupon_code: code,
            coupon_id: couponId || "NA",
          },
        },
      });
      return true;
    } catch (e: any) {
      if (overrideRemoval) {
        setCheckoutModal("NONE");
      } else {
        setIsLoading(false);
      }

      const shouldShowSwitchDialogue = e?.response?.data?.error.code === CombDiscountConflictCode;
      const invalidDiscountCode = e?.response?.data?.error?.data?.invalid_discount_codes;

      if (shouldShowSwitchDialogue) {
        setCouponReplacementConfig({
          showPopup: true,
          appliedDiscountCode: code,
          invalidDiscountCodes: invalidDiscountCode,
          invalidityReason: "DISCOUNTS_NOT_COMBINABLE",
        });
      }

      const errorMsg = e?.response?.data?.error.message;

      if (isManual) {
        setCouponApplyError({
          status: true,
          message: errorMsg,
        });
      } else if (!shouldShowSwitchDialogue) {
        errorToast(errorMsg ?? t("coupon_not_found"));
      }

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_COUPON_FAILED,
        eventType: "flo_action",
        metaData: {
          couponData: {
            coupon_code: code,
            coupon_id: couponId || "NA",
            failed_reason: errorMsg,
          },
        },
      });

      console.error(e);
      return false;
    }
  };

  const handleDeleteCoupon = async (couponToBeDeleted: string) => {
    if (!appliedCoupons?.find((coupon) => coupon?.code === couponToBeDeleted)) return;

    const payload = {
      discount_code: couponToBeDeleted,
    };

    try {
      setIsLoading(true);
      let response: any;
      let discountResponse: any;

      if (isAuthenticated) {
        response = await deleteRequest(DISCOUNT_PATH, payload, "KRATOS_PRIVATE");
        discountResponse = await getRequest(DISCOUNT_PATH, "KRATOS_PRIVATE");
        setCoupons(discountResponse);
      } else {
        response = await deleteRequest(DISCOUNT_PATH, payload, "KRATOS_PUBLIC");
        discountResponse = await getRequest(DISCOUNT_PATH, "KRATOS_PUBLIC");
        setCoupons(discountResponse);
      }

      updateCheckoutBasedOnCheckoutResponse(response);
      setIsLoading(false);

      if (response?.items) {
        setCheckoutItems(getItems(response?.items));
      }

      const couponId = findIdCouponByCode(coupons, couponToBeDeleted);

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_COUPON_REMOVED,
        eventType: "flo_action",
        metaData: {
          couponData: {
            coupon_code: couponToBeDeleted,
            coupon_id: couponId || "NA",
          },
        },
      });

      const shippingHandles = response?.metadata?.available_shipping_handles ?? [];
      const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
      setShippingHandles(shippingHandles as ShippingHandlesType);

      if (
        shippingHandles.length > 0 &&
        showShippingHandles &&
        checkoutView === "PAYMENTS" &&
        initialCheckoutStep !== "PAYMENTS"
      ) {
        openPopup("SHIPPING_HANDLES", {});
      } else {
        mutatePayment();
        mutate(`/checkout/${checkoutId}/rewards`);
        mutate(`UPI_INTENT`);
      }
    } catch (e) {
      setIsLoading(false);
      errorToast(t("delete_discount_error"));
      console.error(e);
    }
  };

  const updatePartnerOfferStateOptimistically = (offerIds: string[], newState: string) => {
    setPartnerOffers(
      partnerOffers.map((o) => (offerIds.includes(o.offer_id) ? { ...o, offer_state: newState } : o)) as typeof partnerOffers,
    );
  };

  const handleCollectPartnerOffer = async (offerIds: string[]) => {
    updatePartnerOfferStateOptimistically(offerIds, "COLLECTED");
    try {
      setIsLoading(true);
      await postRequest(DISCOUNT_PATH, { partner_offer_ids: offerIds }, "KRATOS_PRIVATE");
      mutatePayment();
      mutateOtherOffers();
    } catch (e) {
      updatePartnerOfferStateOptimistically(offerIds, "APPLICABLE");
      errorToast(t("something_went_wrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePartnerOffer = async (offerIds: string[]) => {
    const prevStates = Object.fromEntries(
      partnerOffers.filter((o) => offerIds.includes(o.offer_id)).map((o) => [o.offer_id, o.offer_state]),
    );
    updatePartnerOfferStateOptimistically(offerIds, "APPLICABLE");
    try {
      setIsLoading(true);
      await deleteRequest(DISCOUNT_PATH, { partner_offer_ids: offerIds }, "KRATOS_PRIVATE");
      mutatePayment();
    } catch (e) {
      setPartnerOffers(
        partnerOffers.map((o) => (offerIds.includes(o.offer_id) ? { ...o, offer_state: prevStates[o.offer_id] } : o)) as typeof partnerOffers,
      );
      errorToast(t("something_went_wrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const closeSwitchDiscountPopup = () => {
    setCouponReplacementConfig({
      showPopup: false,
      appliedDiscountCode: "",
      invalidDiscountCodes: [],
      invalidityReason: "NONE",
    });
  };

  const renderStripDisplay = () => {
    if (maxAvailableCoupon) {
      return (
        <MaxDiscountCouponTile
          showCoupons={showCoupons}
          renderCouponSummaryStrip={renderCouponSummaryStrip}
          availableCoupons={availableCoupons}
          maxAvailableCoupon={maxAvailableCoupon}
          handleDiscountApply={handleDiscountApply}
          setCouponApplyError={setCouponApplyError}
          customisations={discountConfig}
          onOpenPartnerOffers={openPartnerOffersDialog}
          onOpen={() => setCouponDialogFilter("ALL")}
          isLoading={isLoading}
          partnerOffersConfig={partnerOffersConfig}
        />
      );
    }

    if (removableAppliedCoupon) {
      return (
        <CouponRemovableTile
          showCoupons={showCoupons}
          removableAppliedCoupon={removableAppliedCoupon}
          availableCoupons={availableCoupons}
          setCouponApplyError={setCouponApplyError}
          handleDeleteCoupon={handleDeleteCoupon}
          renderCouponSummaryStrip={renderCouponSummaryStrip}
          customisations={discountConfig}
          onOpenPartnerOffers={openPartnerOffersDialog}
          onOpen={() => setCouponDialogFilter("ALL")}
          isLoading={isLoading}
          partnerOffersConfig={partnerOffersConfig}
        />
      );
    }

    return (
      <CouponStrip
        showCoupons={showCoupons}
        setCouponApplyError={setCouponApplyError}
        availableCoupons={availableCoupons}
        renderCouponSummaryStrip={renderCouponSummaryStrip}
        customisations={discountConfig}
        onOpenPartnerOffers={openPartnerOffersDialog}
        onOpen={() => setCouponDialogFilter("ALL")}
        partnerOffersConfig={partnerOffersConfig}
      />
    );
  };

  const renderCoupons = () => {
    const isStripDisplayType = getCouponDisplayType() === "STRIP" && (coupons?.length > 0 || partnerOffers?.length > 0);

    if (isStripDisplayType) {
      return <>{renderStripDisplay()}</>;
    }

  //  show Multicoupon if no display coupon permission is false
    if (!showCoupons && !showCouponSummaryStrip) return <MultiGiftCard variant="standalone" />;

    return (
      <CouponInputField
        showCoupons={showCoupons}
        setCouponApplyError={setCouponApplyError}
        handleDiscountApply={handleDiscountApply}
        couponApplyError={couponApplyError}
        renderCouponSummaryStrip={renderCouponSummaryStrip}
        customisations={discountConfig}
        onOpenPartnerOffers={openPartnerOffersDialog}
        partnerOffersConfig={partnerOffersConfig}
      />
    );
  };

  const renderCouponSummaryStrip = () => {
    if (Boolean(currentCouponDisplayConfig?.hideSavings)) return <></>
    return (
      <CouponSummaryStrip
        showRewards={true}
        showSavings={shouldShowSavings()}
        showAppliedCoupons={shouldShowAppliedCoupons()}
        savingItems={savingItems}
        couponDisplayType={getCouponDisplayType() ?? "NONE"}
        handleDeleteCoupon={handleDeleteCoupon}
        showIcon={true}
        customisations={discountConfig}
      />
    );
  };

  // Early returns for special cases


  if (Boolean(isC2P) && !appliedCoupons?.length) {
    return <></>;
  }

  const shouldHideCouponInput =
    hiddenElements?.includes("COUPON_INPUT") || (!merchant?.isDiscountEnabled && appliedCoupons?.length > 0);

  if (shouldHideCouponInput) {
    return (
      <>
        <div className="rounded-2xl overflow-hidden">
          {!Boolean(currentCouponDisplayConfig?.hideSavings) &&
            <CouponSummaryStrip
              showRewards={true}
              savingItems={savingItems}
              couponDisplayType={getCouponDisplayType() ?? "NONE"}
              handleDeleteCoupon={handleDeleteCoupon}
              parent="CHECKOUT"
            />
          }
          <ViewOtherCouponsTile
            availableCoupons={availableCoupons}
            setCouponApplyError={setCouponApplyError}
            customisations={discountConfig}
            onOpenPartnerOffers={openPartnerOffersDialog}
            onOpen={() => setCouponDialogFilter("ALL")}
            partnerOffersConfig={partnerOffersConfig}
          />
          {/* The coupon input is hidden, not the section — gift cards still belong in it. */}
          <div className="bg-green-dark/5 text-green-dark">
            <MultiGiftCard dividerColor="rgba(9, 9, 11, 0.06)" />
          </div>
        </div>
        <GenericDialog
          isOpen={checkoutModal === "DISCOUNT_LIST"}
          setIsOpen={(value: boolean) => {
            setCheckoutModal(value ? "DISCOUNT_LIST" : "NONE");
            if (!value) setCouponDialogFilter("ALL");
          }}
          translateAxis="x"
          modalType="DISCOUNT_LIST"
          customClass="rounded-tl-2xl !rounded-tr-none"
        >
          <CouponDialog
            setOpenDialog={() => {
              setCheckoutModal("NONE");
            }}
            handleDiscountApply={handleDiscountApply}
            handleDeleteCoupon={handleDeleteCoupon}
            isCouponsLoading={isCouponsLoading}
            couponApplyError={couponApplyError}
            setCouponApplyError={setCouponApplyError}
            savingItems={savingItems}
            couponDisplayType={getCouponDisplayType() ?? "NONE"}
            initialFilter={couponDialogFilter}
            onCollectPartnerOffer={handleCollectPartnerOffer}
            onRemovePartnerOffer={handleRemovePartnerOffer}
          />
        </GenericDialog>
      </>
    );
  }

  return (
    <>
      <AnimateLoading loading={isCouponsLoading}>
        <AnimateLoading.Content>
          <div id="coupons-section">
            {(showCoupons || showCouponSummaryStrip) && context === "checkout" && (
              <div className="flex w-full flex-row items-center justify-between px-2 my-3">
                <h2 className="text-text-xs-medium text-coal-light">{t("offers_and_rewards")}</h2>
              </div>
            )}
            {renderCoupons()}
          </div>
        </AnimateLoading.Content>
      </AnimateLoading>

      {activePopup?.key === "CELEBRATION" && (
      <CelebrationPopup
      isOpen={true}
      closePopup={closeCelebrationPopup}
      appliedCouponCode={appliedCouponCode}
      incomingCouponCode={incomingCouponCode}
    />
      )}

      <GenericDialog
        isOpen={couponReplacementConfig?.showPopup}
        translateAxis="y"
        customClass="overflow-scroll md:!top-auto md:absolute"
        dialogOverlay={true}
        modalType="REPLACE_COUPON">
        <SwitchCouponDialog
          appliedDiscountCode={
            couponReplacementConfig?.appliedCouponTitle ??
            couponReplacementConfig.appliedDiscountCode
          }
          invalidDiscountCodes={couponReplacementConfig?.invalidDiscountCodes}
          closePopup={closeSwitchDiscountPopup}
          applyCoupon={async () => {
            closeSwitchDiscountPopup();
            await handleDiscountApply(couponReplacementConfig?.appliedDiscountCode, false, true);
          }}
          invalidReason={couponReplacementConfig?.invalidityReason}
        />
      </GenericDialog>

      <LoaderDialog title={t("replacing_coupons")} />

      {isLoading && <OverlaySpinner />}

      <GenericDialog
        isOpen={checkoutModal === "DISCOUNT_LIST"}
        setIsOpen={(value: boolean) => {
          setCheckoutModal(value ? "DISCOUNT_LIST" : "NONE");
          if (!value) setCouponDialogFilter("ALL");
        }}
        translateAxis="x"
        modalType="DISCOUNT_LIST"
        customClass="rounded-tl-2xl !rounded-tr-none"
        modal={false}
      >
        <CouponDialog
          setOpenDialog={() => {
            setCheckoutModal("NONE");
          }}
          handleDiscountApply={handleDiscountApply}
          handleDeleteCoupon={handleDeleteCoupon}
          isCouponsLoading={isCouponsLoading}
          couponApplyError={couponApplyError}
          setCouponApplyError={setCouponApplyError}
          savingItems={savingItems}
          couponDisplayType={getCouponDisplayType() ?? "NONE"}
          initialFilter={couponDialogFilter}
          onCollectPartnerOffer={handleCollectPartnerOffer}
          onRemovePartnerOffer={handleRemovePartnerOffer}
        />
      </GenericDialog>

      {activePopup?.key === "DISCOUNTED_PRODUCT_SELECTOR" && (
        <DiscountedProductSelectorPopup position={context} />
      )}
    </>
  );
};

export default Coupons;
